import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useContext, useMemo, useState } from 'react';
import { isHeatingModeInZone } from 'console/shared/content/device/heating-modes/z3k/utils';
import { useZ3KDeviceState, useZ3KObjectState } from 'console/shared/hooks/device/z3k';
import { useAppSelector, usePartialAppSelector } from 'shared/hooks/redux/hooks';
import { createSelector } from '@reduxjs/toolkit';
import { useDeviceConfig } from 'console/shared/contexts/device/config';
import { HeatingModeContent, HeatingModeControl } from './types';
import { getTempOrMode } from 'console/schedule-editor/transform-schedule';
import { getClimaticHeatingModesConfig } from 'shared/content/device/heating-modes/z3k/climatic';
import { isClimatic } from 'shared/contexts/device/config/utils';
import { HeatingMode } from 'z3k-parser/model/z3k-config/20-heating-mode';
const empty_array = [];
const select_climatic_modes_loading = createSelector((_, device_id) => device_id, (state) => state.climatic_mode_loading, (device_id, modes_loading) => {
    if (device_id == null) {
        return [];
    }
    const loadings = modes_loading[device_id];
    if (!loadings) {
        return empty_array;
    }
    return Object.values(loadings);
});
const select_z3k_custom_commands = (state) => state.z3k_custom_command;
export const Z3KHeatingModeProvider = memo(function Z3KHeatingModeProvider(props) {
    const { config, heating_circuit_id, children } = props;
    const { id, heating_zones } = config;
    const { device_id, z3k_config, device_type } = useDeviceConfig();
    const z3k_state = useZ3KDeviceState();
    const heating_circuit_state = useZ3KObjectState(heating_circuit_id);
    const z3k_custom_commands = useAppSelector(select_z3k_custom_commands);
    //для климатика с сервера приходят состояния загрузок контуров, а не режимов,
    //поэтому нам надо знать, на какой режим мы нажали
    const [selected_mode_id, setSelectedModeId] = useState(null);
    const active = useMemo(() => {
        if (heating_zones) {
            return heating_zones.some((zone) => isHeatingModeInZone(zone, id, z3k_state, z3k_config));
        }
        return false;
    }, [heating_zones, id, z3k_state, z3k_config]);
    const disabled = useMemo(() => {
        if (heating_zones) {
            return heating_zones.every((zone) => isHeatingModeInZone(zone, id, z3k_state, z3k_config));
        }
        return false;
    }, [heating_zones, id, z3k_state, z3k_config]);
    const heating_circuit_in_mode = useMemo(() => heating_circuit_state.mode_id === id, [heating_circuit_state, id]);
    const { mode_active, mode_disabled } = useMemo(() => {
        if (heating_circuit_id) {
            return {
                mode_active: heating_circuit_in_mode,
                mode_disabled: !heating_circuit_in_mode,
            };
        }
        return { mode_active: active, mode_disabled: disabled };
    }, [heating_circuit_in_mode, heating_circuit_id, active, disabled]);
    const climatic_heating_circuits_loading = usePartialAppSelector(select_climatic_modes_loading, device_id);
    const climatic_mode_loading = useMemo(() => climatic_heating_circuits_loading.some((loading) => loading) &&
        id === selected_mode_id, [climatic_heating_circuits_loading, selected_mode_id, id]);
    const status = useMemo(() => {
        if (!id) {
            return {
                loading: false,
                showDeviceIsOffline: false,
                fail: false,
                fail_message: '',
            };
        }
        if (isClimatic(device_type)) {
            const climatic_heating_modes_config = getClimaticHeatingModesConfig(device_id, z3k_config);
            const config = climatic_heating_modes_config.find((config) => config?.id === id);
            if (heating_circuit_id) {
                return z3k_custom_commands?.by_device_id?.[device_id]?.[heating_circuit_id];
            }
            if (config?.scenario_id) {
                return z3k_custom_commands?.by_device_id?.[device_id]?.[config?.scenario_id];
            }
        }
        const command_id = heating_circuit_id && id === selected_mode_id ? heating_circuit_id : id;
        return z3k_custom_commands?.by_device_id?.[device_id]?.[command_id];
    }, [z3k_custom_commands, device_id, id, heating_circuit_id, selected_mode_id]);
    const loading = useMemo(() => !!(status?.loading &&
        !status?.showDeviceIsOffline &&
        selected_mode_id === id), [status]);
    const heating_mode_state = useMemo(() => ({
        active: mode_active,
        disabled: mode_disabled,
        loading,
        fail: (status?.fail && selected_mode_id === id) ?? false,
        fail_message: status?.fail_message ?? '',
    }), [mode_active, mode_disabled, loading]);
    //настройка температуры для этого контура (может быть id режима)
    const heating_zone = heating_zones?.find(({ heating_circuit }) => heating_circuit == heating_circuit_id);
    let temperature = undefined;
    if (heating_zone) {
        const _temp_or_mode = getTempOrMode(heating_zone.temperature_setting, HeatingMode.get.all(z3k_config));
        if (_temp_or_mode?.temp != null) {
            temperature = _temp_or_mode.temp;
        }
    }
    const heating_mode = useMemo(() => ({
        ...heating_mode_state,
        ...config,
        temperature,
    }), [heating_mode_state, config, temperature]);
    const heating_mode_control = useMemo(() => ({
        setSelectedModeId,
        selected_mode_id,
    }), [setSelectedModeId, selected_mode_id]);
    return (_jsx(HeatingModeContent.Provider, { value: heating_mode, children: _jsx(HeatingModeControl.Provider, { value: heating_mode_control, children: children }) }));
});
export const useHeatingModeControl = () => useContext(HeatingModeControl);
